import {
  AddProjectGroupDocument,
  DeleteProjectGroupDocument,
  GetProjectGroupsDocument,
  type GetProjectGroupsQuery,
  type ProjectGroupInput,
  ReorderProjectGroupsDocument,
  type ReorderProjectGroupsMutation,
  SetProjectUseGroupDocument,
  UpdateProjectGroupDocument,
} from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation, useQuery } from "@apollo/client/react";

export type ProjectGroupItem = NonNullable<GetProjectGroupsQuery["project"]>["groups"][number];

/**
 * Hook for managing a project's groups.
 *
 * Each project owns its groups, which cover the lessons directly under it; a new child
 * starts from a copy of its parent's and diverges after. The `useGroup` flag lets clients
 * (e.g. b2b-mobile) decide whether to render slides grouped by these definitions.
 *
 * All mutations return the updated `Project` entity ({ id, groups, useGroup }),
 * so Apollo normalises by id and the `GetProjectGroups` query updates on its own —
 * no manual refetch required.
 */
export function useProjectGroups(projectId: string) {
  const { data, loading, error } = useQuery(GetProjectGroupsDocument, {
    variables: { id: projectId },
    skip: !projectId,
    fetchPolicy: "cache-and-network",
  });

  const [addProjectGroupMutation, { loading: adding }] = useMutation(AddProjectGroupDocument);
  const [updateProjectGroupMutation, { loading: updating }] = useMutation(
    UpdateProjectGroupDocument
  );
  const [deleteProjectGroupMutation] = useMutation(DeleteProjectGroupDocument);
  const [setProjectUseGroupMutation] = useMutation(SetProjectUseGroupDocument);
  const [reorderProjectGroupsMutation] = useMutation(ReorderProjectGroupsDocument);

  const addGroup = (input: ProjectGroupInput) =>
    withToast(
      () => addProjectGroupMutation({ variables: { projectId, input } }),
      {
        loading: "Creating group...",
        success: `Created "${input.name.trim()}"`,
        error: (error) => (error instanceof Error ? error.message : "Failed to create group."),
      },
      "addProjectGroup"
    );

  const updateGroup = (groupId: string, input: ProjectGroupInput) =>
    withToast(
      () => updateProjectGroupMutation({ variables: { projectId, groupId, input } }),
      {
        loading: "Saving group...",
        success: `Saved "${input.name.trim()}"`,
        error: (error) => (error instanceof Error ? error.message : "Failed to save group."),
      },
      "updateProjectGroup"
    );

  const deleteGroup = (groupId: string) =>
    withToast(
      () => deleteProjectGroupMutation({ variables: { projectId, groupId } }),
      {
        loading: "Deleting group...",
        success: "Group deleted",
        error: (error) => (error instanceof Error ? error.message : "Failed to delete group."),
      },
      "deleteProjectGroup"
    );

  const setUseGroup = (useGroup: boolean) =>
    withToast(
      () => setProjectUseGroupMutation({ variables: { projectId, useGroup } }),
      {
        loading: useGroup ? "Turning groups on..." : "Turning groups off...",
        success: useGroup ? "Groups on" : "Groups off",
        error: (error) =>
          error instanceof Error ? error.message : "Failed to change the group setting.",
      },
      "setProjectUseGroup"
    );

  const project = data?.project;

  // Takes the full list of groups in their new order. An optimistic response keeps
  // the dragged order on screen instead of snapping back until the server responds.
  const reorderGroups = (orderedGroups: ProjectGroupItem[]) =>
    reorderProjectGroupsMutation({
      variables: { projectId, groupIds: orderedGroups.map((group) => group.id) },
      optimisticResponse: {
        reorderProjectGroups: {
          __typename: "Project",
          id: projectId,
          useGroup: project?.useGroup ?? false,
          groups: orderedGroups.map((group, index) => ({ ...group, sortOrder: index })),
        },
      } as ReorderProjectGroupsMutation,
    });

  return {
    groups: project?.groups ?? [],
    useGroup: project?.useGroup ?? false,
    loading,
    error,
    adding,
    updating,
    addGroup,
    updateGroup,
    deleteGroup,
    setUseGroup,
    reorderGroups,
  };
}
